import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useSearchAllQuery } from "marketplace/_api/marketplace-api";
import { MarketplaceResultType } from "config";
import { useGetCompanyBookmarksQuery, useGetIndividualBookmarksQuery, useGetTeamBookmarksQuery } from "marketplace/bookmarks/_api/bookmark-api";
import CompanyBookmarkQuery, {
  COMPANY_BOOKMARK_QUERY_MAXIMUM_LENGTH,
} from "marketplace/bookmarks/company-bookmarks/_api/queries/company-bookmark-query";
import IndividualBookmarkQuery, {
  INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH,
} from "marketplace/bookmarks/individual-bookmarks/_api/queries/individual-bookmark-query";
import TeamBookmarkQuery, { TEAM_BOOKMARK_QUERY_MAXIMUM_LENGTH } from "marketplace/bookmarks/team-bookmarks/_api/queries/team-bookmark-query";
import { useGetCompaniesByLegalEntityIdsQuery, useGetCompanyIndividualsQuery } from "marketplace/companies/_api/company-api";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { useMemo } from "react";
import UserCoworkerQuery, {
  USER_COWORKER_QUERY_MAXIMUM_LENGTH,
  UserCoworkerFilterParameters,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { useGetUserCoworkersQuery } from "users/information/coworker-information/_api/user-coworker-api";
import NetworkConnectionQuery, {
  NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH,
  NetworkConnectionOrderField,
} from "users/networking/connections/_api/queries/network-connection-query";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";
import { useSession } from "users/__session/session-context";
import {
  personFromCoworker,
  personFromIndividual,
  personFromNetworkConnection,
  personFromSearchHit,
  personFromSessionUser,
  type TeamPickerPerson,
  worksFor,
} from "work/work-agreements/teams/people-picker/team-picker-person";
import { type TeamLeaderFirm, UNNAMED_LEADER_FIRM } from "work/work-agreements/teams/people-picker/team-people-sources";

/**
 * How many people, teams or companies one source shows at once. The picker is a list someone reads
 * and checks, not a directory, so one page of the server's largest is the whole of it; a source with
 * more says so and asks for a narrower filter.
 */
export const TEAM_PEOPLE_PAGE_SIZE = 100;

/**
 * The longest filter every source accepts. The coworker, network and bookmark query classes throw on
 * anything longer, so the picker holds its filter to this and never builds a query past it.
 */
export const TEAM_PEOPLE_FILTER_MAXIMUM_LENGTH = Math.min(
  USER_COWORKER_QUERY_MAXIMUM_LENGTH,
  NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH,
  INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH,
  TEAM_BOOKMARK_QUERY_MAXIMUM_LENGTH,
  COMPANY_BOOKMARK_QUERY_MAXIMUM_LENGTH,
);

/** What the picker says in place of the leader's firm when it has no Marketplace profile. */
export const LEADER_FIRM_MISSING_REASON = "This firm has no Marketplace profile";
/** What the picker says in place of the leader's firm when the viewer may not read its profile. */
export const LEADER_FIRM_UNAUTHORIZED_REASON = "You do not have access to this firm's Marketplace profile";

/** What the picker says in place of a bookmark the viewer may no longer read. */
export const BOOKMARK_UNAVAILABLE_REASON = "You no longer have access to this bookmark";

/**
 * One row of a source's results: a person, a group of people that is checked as one (a marketplace
 * team, whose people arrive with it, or a company, whose people are fetched when it is opened), or a
 * bookmark the viewer can no longer read — shown with the reason, never silently left out.
 */
export type TeamPickerEntry =
  | { kind: "person"; key: string; person: TeamPickerPerson }
  | { kind: "team"; key: string; name: string; people: TeamPickerPerson[] }
  | { kind: "company"; key: string; companyId: string; name: string }
  | { kind: "unavailable"; key: string; name: string; reason: string };

export type TeamPeopleResults = {
  entries: TeamPickerEntry[];
  isLoading: boolean;
  isError: boolean;
  /** Whether the source holds more than {@link TEAM_PEOPLE_PAGE_SIZE} matches. */
  isTruncated: boolean;
};

const FIRST_PAGE = PaginationParameters.create({ pageIndex: 0, pageSize: TEAM_PEOPLE_PAGE_SIZE });

function isTruncated<T>(page: Paginated<T> | undefined): boolean {
  if (!page) return false;
  return page.headers.totalElements > page.data.length;
}

function personEntry(person: TeamPickerPerson): TeamPickerEntry {
  return { kind: "person", key: `person-${person.userId}`, person };
}

function textOrUndefined(filter: string): string | undefined {
  return filter.trim() || undefined;
}

/**
 * The entries with nobody from this legal entity in them: the client's own firm, when the client
 * staffs the vendor team (#6859). A team keeps the rest of its people, and goes when nobody is left.
 * A company's people are not known until it is opened, so `TeamPeopleResultsList` leaves them out
 * there.
 */
export function withoutPeopleOf(entries: readonly TeamPickerEntry[], legalEntityId: string | undefined): TeamPickerEntry[] {
  if (!legalEntityId) return [...entries];
  return entries.flatMap((entry): TeamPickerEntry[] => {
    if (entry.kind === "person") return worksFor(entry.person, legalEntityId) ? [] : [entry];
    if (entry.kind !== "team") return [entry];
    const people = entry.people.filter((person) => !worksFor(person, legalEntityId));
    return people.length ? [{ ...entry, people }] : [];
  });
}

// Each hook memoizes its whole argument. The bookmark, coworker and network queries are classes,
// and RTK Query keeps only a plain argument from one render to the next: a fresh instance each
// render would be a new request each render once one failed (see app/CLAUDE.md).

/** Whether the filter, as the coworker search reads it, would find this person by name. */
function nameMatches(person: TeamPickerPerson, filter: string): boolean {
  const text = textOrUndefined(filter)?.toLowerCase();
  return !text || person.name.toLowerCase().includes(text);
}

/**
 * The firm's people, the signed-in user first. `GET users/coworkers` leaves the caller out, so the
 * caller is added here, with their own marketplace record read for credentials as anybody else's is:
 * the picker's rules then treat them like any coworker, and a sole practitioner can choose themselves
 * to lead.
 */
export function useCoworkerResults(filter: string): TeamPeopleResults {
  const caller = useSession().user;
  const callerUserId = caller?.id?.value;
  const callerIds = useMemo(() => (callerUserId ? { userIds: [callerUserId] } : skipToken), [callerUserId]);
  const callerRecord = useGetIndividualsByUserIdsQuery(callerIds);
  const self = personFromSessionUser(caller, callerRecord.data?.find((individual) => individual.userId === callerUserId));
  const args = useMemo(
    () => ({
      query: new UserCoworkerQuery(
        new UserCoworkerFilterParameters(),
        new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
        FIRST_PAGE,
        textOrUndefined(filter),
      ),
      append: false,
    }),
    [filter],
  );
  const result = useGetUserCoworkersQuery(args);
  const coworkers = (result.data?.data ?? []).map((coworker) => personEntry(personFromCoworker(coworker)));
  return {
    entries: self && nameMatches(self, filter) ? [personEntry(self), ...coworkers] : coworkers,
    isLoading: result.isFetching,
    isError: result.isError,
    isTruncated: isTruncated(result.data),
  };
}

/**
 * The viewer's network. A connection does not say whether the person's profile lists credentials,
 * so their marketplace records are looked up beside it, as the network tab of the old picker did.
 */
export function useNetworkResults(filter: string): TeamPeopleResults {
  const args = useMemo(
    () => ({
      query: new NetworkConnectionQuery({
        paginationParameters: FIRST_PAGE,
        orderBy: NetworkConnectionOrderField.Name,
        orderDir: OrderDirection.Ascending,
        query: textOrUndefined(filter),
      }),
    }),
    [filter],
  );
  const result = useGetUserNetworkConnectionsQuery(args);
  const connections = result.data?.data;
  const userIds = useMemo(() => (connections ?? []).map((connection) => connection.id), [connections]);
  const individuals = useGetIndividualsByUserIdsQuery(userIds.length ? { userIds } : skipToken);
  const byUserId = new Map((individuals.data ?? []).map((individual) => [individual.userId, individual]));
  return {
    entries: (connections ?? []).map((connection) =>
      personEntry(personFromNetworkConnection(connection, byUserId.get(connection.id))),
    ),
    isLoading: result.isFetching,
    isError: result.isError,
    isTruncated: isTruncated(result.data),
  };
}

/**
 * The people in search hits who can be added. An individual hit always carries the two ids a team
 * member is made of; a team's nested leader and members may not, and may be missing altogether.
 */
function searchPeople(hits: readonly (Parameters<typeof personFromSearchHit>[0] | null | undefined)[]): TeamPickerPerson[] {
  return hits.flatMap((hit) => {
    const person = hit ? personFromSearchHit(hit) : undefined;
    return person ? [person] : [];
  });
}

/**
 * Marketplace search: people and teams, no companies — a company's people are reached through its
 * bookmark. Nothing is asked until there is something to search for.
 */
export function useMarketplaceSearchResults(query: string): TeamPeopleResults {
  const text = textOrUndefined(query);
  const args = useMemo(() => (text ? { search: { query: text, hideCompanies: true, from: 0, size: TEAM_PEOPLE_PAGE_SIZE } } : skipToken), [text]);
  const result = useSearchAllQuery(args);
  const hits = (result.data?.hits ?? []).map((hit) => hit.data);
  return {
    entries: hits.flatMap((hit): TeamPickerEntry[] => {
      if (hit.type !== MarketplaceResultType.Team) return searchPeople([hit]).map(personEntry);
      const people = searchPeople([hit.leader, ...(hit.members ?? [])]);
      return [{ kind: "team", key: `team-${hit.id}`, name: hit.name ?? "Unnamed team", people }];
    }),
    isLoading: result.isFetching,
    isError: result.isError,
    isTruncated: (result.data?.total ?? hits.length) > hits.length,
  };
}

export function useBookmarkedIndividualResults(filter: string): TeamPeopleResults {
  const args = useMemo(
    () => ({ query: new IndividualBookmarkQuery({ paginationParameters: FIRST_PAGE, query: textOrUndefined(filter) }) }),
    [filter],
  );
  const result = useGetIndividualBookmarksQuery(args);
  return {
    entries: (result.data?.data ?? []).map((item): TeamPickerEntry =>
      item.individual
        ? personEntry(personFromIndividual(item.individual))
        : { kind: "unavailable", key: `bookmark-${item.id}`, name: "Bookmarked individual", reason: BOOKMARK_UNAVAILABLE_REASON },
    ),
    isLoading: result.isFetching,
    isError: result.isError,
    isTruncated: isTruncated(result.data),
  };
}

export function useBookmarkedTeamResults(filter: string): TeamPeopleResults {
  const args = useMemo(
    () => ({ query: new TeamBookmarkQuery({ paginationParameters: FIRST_PAGE, query: textOrUndefined(filter) }) }),
    [filter],
  );
  const result = useGetTeamBookmarksQuery(args);
  return {
    entries: (result.data?.data ?? []).map((item): TeamPickerEntry => {
      if (!item.team) {
        return { kind: "unavailable", key: `bookmark-${item.id}`, name: "Bookmarked team", reason: BOOKMARK_UNAVAILABLE_REASON };
      }
      const people = [item.team.leader, ...item.team.members].map(personFromIndividual);
      return { kind: "team", key: `team-${item.team.id}`, name: item.team.name ?? "Unnamed team", people };
    }),
    isLoading: result.isFetching,
    isError: result.isError,
    isTruncated: isTruncated(result.data),
  };
}

export function useBookmarkedCompanyResults(filter: string): TeamPeopleResults {
  const args = useMemo(
    () => ({ query: new CompanyBookmarkQuery({ paginationParameters: FIRST_PAGE, query: textOrUndefined(filter) }) }),
    [filter],
  );
  const result = useGetCompanyBookmarksQuery(args);
  return {
    entries: (result.data?.data ?? []).map((item): TeamPickerEntry =>
      item.company
        ? { kind: "company", key: `company-${item.company.id}`, companyId: item.company.id, name: item.company.name ?? "Unnamed company" }
        : { kind: "unavailable", key: `bookmark-${item.id}`, name: "Bookmarked company", reason: BOOKMARK_UNAVAILABLE_REASON },
    ),
    isLoading: result.isFetching,
    isError: result.isError,
    isTruncated: isTruncated(result.data),
  };
}

/**
 * The people of the vendor leader's firm, for a client staffing that firm's team (#6860). A client
 * cannot list another firm's roster, so they are read as that firm's marketplace company: its
 * published profiles, the same people a bookmarked company lists. The answer has no filter, so the
 * filter is applied here, by name, as the coworker search reads it.
 *
 * A firm with no company, or one the viewer may not see, is one row that says so, never an empty list.
 */
export function useLeaderColleagueResults(firm: TeamLeaderFirm, filter: string): TeamPeopleResults {
  const firmArgs = useMemo(() => ({ legalEntityIds: [firm.legalEntityId] }), [firm.legalEntityId]);
  const companies = useGetCompaniesByLegalEntityIdsQuery(firmArgs);
  const company = companies.data?.found[0];
  const individuals = useGetCompanyIndividualsQuery(company ? { companyId: company.id } : skipToken);
  const unavailable = companies.data?.unavailable[0];
  const status = {
    isLoading: companies.isFetching || individuals.isFetching,
    isError: companies.isError || individuals.isError,
    isTruncated: false,
  };
  if (unavailable) {
    const reason = unavailable.reason === "unauthorized" ? LEADER_FIRM_UNAUTHORIZED_REASON : LEADER_FIRM_MISSING_REASON;
    const name = firm.name?.trim() || UNNAMED_LEADER_FIRM;
    return { ...status, entries: [{ kind: "unavailable", key: `leader-firm-${firm.legalEntityId}`, name, reason }] };
  }
  const people = (individuals.data ?? []).map(personFromIndividual).filter((person) => nameMatches(person, filter));
  return { ...status, entries: people.map(personEntry) };
}

/**
 * A company's people, fetched only once the company row is opened or checked: a list of bookmarked
 * companies should not cost a request per company before anyone asks.
 */
export function useCompanyPeople(companyId: string, wanted: boolean) {
  const result = useGetCompanyIndividualsQuery(wanted ? { companyId } : skipToken);
  const people = useMemo(() => (result.data ?? []).map(personFromIndividual), [result.data]);
  return { people, isLoading: result.isFetching, isError: result.isError, isLoaded: result.isSuccess };
}
